import {
  SYSTEM_MANAGEMENT_ENDPOINTS,
  build_system_management_url,
} from '@/constants/system_management_endpoints';
import {
  normalize_configuration,
  // normalize_access_assignment,
} from './platform_management_helpers';

const PLATFORM_MANAGEMENT_ENDPOINT_KEYS = Object.freeze({
  GET_PLATFORMS: 'GET_PLATFORMS',
  GET_PLATFORM_CONFIGS: 'GET_PLATFORM_CONFIGS',
  // GET_PLATFORM_ACCESS_ASSIGNMENTS: 'GET_PLATFORM_ACCESS_ASSIGNMENTS',
  CREATE_PLATFORM: 'CREATE_PLATFORM',
  CREATE_PLATFORM_CONFIG: 'CREATE_PLATFORM_CONFIG',
  ASSIGN_PLATFORM_ACCESS: 'ASSIGN_PLATFORM_ACCESS',
  PLATFORM_READINESS_CHECK: 'PLATFORM_READINESS_CHECK',
  LAUNCH_CONFIGURED_PLATFORM: 'LAUNCH_CONFIGURED_PLATFORM',
  PLATFORM_CONFIG_DEPLOYMENT_HANDOVER: 'PLATFORM_CONFIG_DEPLOYMENT_HANDOVER',
  REVEAL_PLATFORM_CONFIG_DEPLOYMENT_VALUES: 'REVEAL_PLATFORM_CONFIG_DEPLOYMENT_VALUES',
  SYNC_PLATFORM_CONFIG_TO_GITHUB: 'SYNC_PLATFORM_CONFIG_TO_GITHUB',
  SYNC_PLATFORM_CONFIG_TO_CICD: 'SYNC_PLATFORM_CONFIG_TO_CICD',
});
const PLATFORM_MANAGEMENT_ENDPOINT_MAP = new Map([
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.GET_PLATFORMS,
    SYSTEM_MANAGEMENT_ENDPOINTS.get_platforms,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.GET_PLATFORM_CONFIGS,
    SYSTEM_MANAGEMENT_ENDPOINTS.get_platform_configs,
  ],
  // [
  //   PLATFORM_MANAGEMENT_ENDPOINT_KEYS.GET_PLATFORM_ACCESS_ASSIGNMENTS,
  //   SYSTEM_MANAGEMENT_ENDPOINTS.get_platform_access_assignments,
  // ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.CREATE_PLATFORM,
    SYSTEM_MANAGEMENT_ENDPOINTS.create_platform,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.CREATE_PLATFORM_CONFIG,
    SYSTEM_MANAGEMENT_ENDPOINTS.create_platform_config,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.ASSIGN_PLATFORM_ACCESS,
    SYSTEM_MANAGEMENT_ENDPOINTS.assign_platform_access,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.PLATFORM_READINESS_CHECK,
    SYSTEM_MANAGEMENT_ENDPOINTS.platform_readiness_check,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.LAUNCH_CONFIGURED_PLATFORM,
    SYSTEM_MANAGEMENT_ENDPOINTS.launch_configured_platform,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.PLATFORM_CONFIG_DEPLOYMENT_HANDOVER,
    SYSTEM_MANAGEMENT_ENDPOINTS.platform_config_deployment_handover,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.REVEAL_PLATFORM_CONFIG_DEPLOYMENT_VALUES,
    SYSTEM_MANAGEMENT_ENDPOINTS.reveal_platform_config_deployment_values,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.SYNC_PLATFORM_CONFIG_TO_GITHUB,
    SYSTEM_MANAGEMENT_ENDPOINTS.sync_platform_config_to_github,
  ],
  [
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.SYNC_PLATFORM_CONFIG_TO_CICD,
    SYSTEM_MANAGEMENT_ENDPOINTS.sync_platform_config_to_cicd,
  ],
]);

function parse_response_json(response) {
  return response.json().catch(() => null);
}

function get_platform_management_endpoint(endpoint_key) {
  if (!PLATFORM_MANAGEMENT_ENDPOINT_MAP.has(endpoint_key)) {
    throw new Error('Invalid platform management endpoint.');
  }

  return PLATFORM_MANAGEMENT_ENDPOINT_MAP.get(endpoint_key);
}

function build_safe_platform_management_url(endpoint_key) {
  const endpoint = get_platform_management_endpoint(endpoint_key);
  const raw_url = build_system_management_url(endpoint);
  const parsed_url = new URL(raw_url, window.location.origin);
  const allowed_origin = new URL(process.env.VUE_APP_API_BASE_URL || window.location.origin, window.location.origin).origin;

  if (parsed_url.origin !== allowed_origin) {
    throw new Error('Unsafe platform management URL origin.');
  }

  if (parsed_url.pathname !== endpoint) {
    throw new Error('Unsafe platform management URL path.');
  }

  return parsed_url.toString();
}

async function fetch_platform_management_endpoint(endpoint_key, options) {
  const safe_platform_management_url = build_safe_platform_management_url(endpoint_key);
  // Security: endpoint_key is resolved through a fixed internal allowlist before fetch is called.
  return fetch(safe_platform_management_url, options);
}

function build_request_options(method, headers, body = null) {
  const options = {
    method,
    credentials: 'include',
    headers,
  };

  if (body) options.body = JSON.stringify(body);
  return options;
}

export function build_platform_management_headers(token, csrf_token, include_page_url = true) {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Token ${token}`,
    'X-CSRFToken': csrf_token,
  };

  if (include_page_url) headers['X-Page-URL'] = window.location.href;
  return headers;
}

async function perform_platform_management_request(endpoint_key, method, headers, payload = null) {
  const response = await fetch_platform_management_endpoint(
    endpoint_key,
    build_request_options(method, headers, payload),
  );
  const response_data = await parse_response_json(response);

  return {
    ok: response.ok && response_data?.status === 'success',
    response_data,
  };
}

async function fetch_normalized_collection(endpoint_key, headers, normalize_item) {
  const result = await perform_platform_management_request(endpoint_key, 'GET', headers);
  const response_items = Array.isArray(result.response_data?.data) ? result.response_data.data : [];

  return {
    ...result,
    data: response_items.map((item) => normalize_item(item)),
  };
}

export async function fetch_platforms_api(headers) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.GET_PLATFORMS,
    'GET',
    headers,
  );
}

export async function fetch_platform_configurations_api(headers) {
  return fetch_normalized_collection(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.GET_PLATFORM_CONFIGS,
    headers,
    normalize_configuration,
  );
}

// export async function fetch_platform_access_assignments_api(headers) {
//   return fetch_normalized_collection(
//     PLATFORM_MANAGEMENT_ENDPOINT_KEYS.GET_PLATFORM_ACCESS_ASSIGNMENTS,
//     headers,
//     normalize_access_assignment,
//   );
// }

export async function submit_platform_form_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.CREATE_PLATFORM,
    'POST',
    headers,
    payload,
  );
}

export async function submit_platform_config_form_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.CREATE_PLATFORM_CONFIG,
    'POST',
    headers,
    payload,
  );
}

export async function submit_platform_access_form_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.ASSIGN_PLATFORM_ACCESS,
    'POST',
    headers,
    payload,
  );
}

export async function run_readiness_check_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.PLATFORM_READINESS_CHECK,
    'POST',
    headers,
    payload,
  );
}

export async function test_launch_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.LAUNCH_CONFIGURED_PLATFORM,
    'POST',
    headers,
    payload,
  );
}

export async function fetch_platform_config_deployment_handover_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.PLATFORM_CONFIG_DEPLOYMENT_HANDOVER,
    'POST',
    headers,
    payload,
  );
}

export async function reveal_platform_config_deployment_values_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.REVEAL_PLATFORM_CONFIG_DEPLOYMENT_VALUES,
    'POST',
    headers,
    payload,
  );
}

// Legacy sync helper retained for older screens only; Platform Management now uses DevOps handover.
export async function sync_platform_config_to_github_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.SYNC_PLATFORM_CONFIG_TO_GITHUB,
    'POST',
    headers,
    payload,
  );
}

// Legacy CI/CD helper retained for older screens only; it is not part of the DevOps handover flow.
export async function sync_platform_config_to_cicd_api(headers, payload) {
  return perform_platform_management_request(
    PLATFORM_MANAGEMENT_ENDPOINT_KEYS.SYNC_PLATFORM_CONFIG_TO_CICD,
    'POST',
    headers,
    payload,
  );
}
